<template>
  <DefaultLayout>
    <template v-slot:header>
      <CoastHeader :hasBackButton="true" :is-modal="!!selectedDraft" :title="$t('Algae Wash-up')"></CoastHeader>
    </template>
    <FormControl @onSubmit="handleSubmit" autocomplete="off" ref="FormControl">

      <ion-item lines="none" class="warning">
        {{ $t('WARNING: Some algae is toxic and can be harmful to humans and animals. Please no NOT touch algae unless you are wearing the proper protective equipment') }}
      </ion-item>

      <ion-item lines="none">
        <a href="https://www.lakehuron.ca/algae-field-guide" target="_blank">{{ $t('Algae Field Guide') }}</a>
      </ion-item>

      <FieldWrapper ionItemClass="camera-item" name="files">
        <UploadImage @input="setImages" :images="files"></UploadImage>
      </FieldWrapper>

      <FieldWrapper>
        <ion-text lines="none" color="primary">
          Current Date: {{ currentDatetime.date }}, {{ formatAMPM(currentDatetime.time) }}
        </ion-text>
      </FieldWrapper>

      <ion-item class="submission-text" lines="none">
        <ion-row>
          <ion-text>{{ $t('Was the observation date different?') }}</ion-text>
          <ion-text @click="toggleDatetime(true)" class="add" color="primary" v-if="!differentDate">
            {{ $t('+ Add another date') }}
          </ion-text>
          <ion-text @click="toggleDatetime(false)" class="add" color="primary" v-else>
            {{ $t('- Remove the observation date') }}
          </ion-text>
        </ion-row>
      </ion-item>

      <FieldWrapper v-if="differentDate" :label="$t('Date *')" ionItemClass="border-full" name="submitted_at">
        <ion-datetime :placeholder="$t('Select Date')" @ionChange="hideError('submitted_at')"
                      presentation="month-day-year"
                      v-model="datetime.date"></ion-datetime>
        <Calendar class="date-icon"></Calendar>
      </FieldWrapper>

      <FieldWrapper v-if="differentDate" :label="$t('Time *')" ionItemClass="border-full" name="submitted_at">
        <ion-datetime :placeholder="$t('Select Time')" @ionChange="hideError('submitted_at')" display-format="HH:mm"
                      v-model="datetime.time"></ion-datetime>
        <Time class="time-icon"></Time>
      </FieldWrapper>

      <FieldWrapper :label="$t('Are there algae in the water?')" ionItemClass="border-full" name="beachTypeExists">
        <ion-radio-group class="radio-group" v-model="algaeInWater" name="algae_in_water">
          <ion-item class="coast-radio" lines="none">
            <ion-radio :value="true" slot="start"></ion-radio>
            <ion-label>{{ $t('Yes') }}</ion-label>
          </ion-item>

          <ion-item class="coast-radio" lines="none">
            <ion-radio :value="false" slot="start"></ion-radio>
            <ion-label>{{ $t('No') }}</ion-label>
          </ion-item>
        </ion-radio-group>
      </FieldWrapper>

      <FieldWrapper v-if="algaeInWater" :label="$t('Algae in water length (m) *')" ionItemClass="border-full"
                    name="algae_in_water_length">
        <ion-input :placeholder="$t('Algae in water length')" @ionChange="hideError('algae_in_water_length')"
                   inputmode="decimal"
                   pattern="^(?:\d+(?:[.,]\d{1,2})?|\d+)$"
                   v-model="form.algae_in_water_length"></ion-input>
      </FieldWrapper>

      <FieldWrapper v-if="algaeInWater" :label="$t('Algae in water width (m) *')" ionItemClass="border-full"
                    name="algae_in_water_width">
        <ion-input :placeholder="$t('Algae in water width')" @ionChange="hideError('algae_in_water_width')"
                   inputmode="decimal"
                   pattern="^(?:\d+(?:[.,]\d{1,2})?|\d+)$"
                   v-model="form.algae_in_water_width"></ion-input>
      </FieldWrapper>

      <FieldWrapper :label="$t('Are there algae on beach?')" ionItemClass="border-full" name="algaeOnBeach">
        <ion-radio-group class="radio-group" v-model="algaeOnBeach">
          <ion-item class="coast-radio" lines="none">
            <ion-radio :value="true" slot="start"></ion-radio>
            <ion-label>{{ $t('Yes') }}</ion-label>
          </ion-item>

          <ion-item class="coast-radio" lines="none">
            <ion-radio :value="false" slot="start"></ion-radio>
            <ion-label>{{ $t('No') }}</ion-label>
          </ion-item>
        </ion-radio-group>
      </FieldWrapper>

      <FieldWrapper v-if="algaeOnBeach" :label="$t('Algae on beach length (m) *')" ionItemClass="border-full"
                    name="algae_on_beach_length">
        <ion-input :placeholder="$t('Algae on beach length')" @ionChange="hideError('algae_on_beach_length')"
                   inputmode="decimal"
                   pattern="^(?:\d+(?:[.,]\d{1,2})?|\d+)$"
                   v-model="form.algae_on_beach_length"></ion-input>
      </FieldWrapper>

      <FieldWrapper v-if="algaeOnBeach" :label="$t('Algae on beach width (m) *')" ionItemClass="border-full"
                    name="algae_on_beach_width">
        <ion-input :placeholder="$t('Algae on beach width')" @ionChange="hideError('algae_on_beach_width')"
                   inputmode="decimal"
                   pattern="^(?:\d+(?:[.,]\d{1,2})?|\d+)$"
                   v-model="form.algae_on_beach_width"></ion-input>
      </FieldWrapper>

      <FieldWrapper :label="$t('Algae category *')" ionItemClass="border-full" name="category">
        <ion-select :placeholder="$t('Select')" @ionChange="hideError('category')" interface="action-sheet"
                    v-model="category">
          <ion-select-option :key="i" :value="i" v-for="i in categories">
            {{ i.name }}
          </ion-select-option>
        </ion-select>
        <ChevronDown class="chevron-down-icon"></ChevronDown>
      </FieldWrapper>

      <ion-item v-if="category?.description" lines="none">
        {{ category.description }}
      </ion-item>

      <FieldWrapper v-if="category && category.name === 'Other/Unknown'"
                    :label="$t('Other/Unknown *')"
                    ionItemClass="border-full"
                    name="category_other">
        <ion-input :placeholder="$t('Other/Unknown')" @ionChange="hideError('category_other')"
                   v-model="form.category_other"></ion-input>
      </FieldWrapper>

      <FieldWrapper :label="$t('Algae colour *')" ionItemClass="border-full" name="colour">
        <ion-select :placeholder="$t('Select')" @ionChange="hideError('colour')" interface="action-sheet"
                    v-model="form.colour">
          <ion-select-option :key="i" :value="i" v-for="i in colours">{{ i }}</ion-select-option>
        </ion-select>
        <ChevronDown class="chevron-down-icon"></ChevronDown>
      </FieldWrapper>

      <FieldWrapper v-if="form.colour && form.colour.includes('None of the above')" :label="$t('Other colour *')"
                    ionItemClass="border-full" name="other_colour">
        <ion-input :placeholder="$t('Other')" @ionChange="hideError('other_colour')"
                   type="text"
                   v-model="form.other_colour"></ion-input>
      </FieldWrapper>

      <FieldWrapper :label="$t('Visual observation')" ionItemClass="border-full" name="visual_observation">
        <ion-select multiple="true" :placeholder="$t('Select')" @ionChange="hideError('visual_observation')"
                    interface="action-sheet"
                    v-model="form.visual_observation">
          <ion-select-option :key="i" :value="i" v-for="i in visualObservations">{{ i }}</ion-select-option>
        </ion-select>
        <ChevronDown class="chevron-down-icon"></ChevronDown>
      </FieldWrapper>

      <FieldWrapper :label="$t('Odour *')" ionItemClass="border-full" name="odour">
        <ion-select :placeholder="$t('Select')" @ionChange="hideError('odour')" interface="action-sheet"
                    v-model="form.odour">
          <ion-select-option :key="i" :value="i" v-for="i in odours">{{ i }}</ion-select-option>
        </ion-select>
        <ChevronDown class="chevron-down-icon"></ChevronDown>
      </FieldWrapper>

      <FieldWrapper v-if="form.odour && form.odour.includes('Other')" :label="$t('Other odour *')"
                    ionItemClass="border-full" name="other_odour">
        <ion-input :placeholder="$t('Other')" @ionChange="hideError('other_odour')"
                   type="text"
                   v-model="form.other_odour"></ion-input>
      </FieldWrapper>

      <FieldWrapper :label="$t('Environment *')" ionItemClass="border-full" name="environment">
        <ion-select :placeholder="$t('Select')" @ionChange="hideError('environment')" interface="action-sheet"
                    v-model="form.environment">
          <ion-select-option :key="i" :value="i" v-for="i in environments">{{ i }}</ion-select-option>
        </ion-select>
        <ChevronDown class="chevron-down-icon"></ChevronDown>
      </FieldWrapper>

      <FieldWrapper v-if="form.environment && form.environment.includes('Other')" :label="$t('Other environment *')"
                    ionItemClass="border-full" name="other_environment">
        <ion-input :placeholder="$t('Other')" @ionChange="hideError('other_environment')"
                   type="text"
                   v-model="form.other_environment"></ion-input>
      </FieldWrapper>

      <FieldWrapper :label="$t('Are the algae attached to an object? (Ex. rock) *')" ionItemClass="border-full"
                    name="attachedToObject">
        <ion-radio-group class="radio-group" v-model="attachedToObject">
          <ion-item class="coast-radio" lines="none">
            <ion-radio :value="true" slot="start"></ion-radio>
            <ion-label>{{ $t('Yes') }}</ion-label>
          </ion-item>

          <ion-item class="coast-radio" lines="none">
            <ion-radio :value="false" slot="start"></ion-radio>
            <ion-label>{{ $t('No') }}</ion-label>
          </ion-item>
        </ion-radio-group>
      </FieldWrapper>

      <FieldWrapper v-if="attachedToObject" :label="$t('What is the object?')" ionItemClass="border-full"
                    name="attached_object">
        <ion-input :placeholder="$t('Object')" @ionChange="hideError('attached_object')"
                   type="text"
                   v-model="form.attached_object"></ion-input>
      </FieldWrapper>

      <FieldWrapper :label="$t('Notes')" ionItemClass="border-full">
        <ion-textarea :placeholder="$t('Enter any notes here...')" cols="20" rows="7"
                      v-model="form.notes"></ion-textarea>
      </FieldWrapper>

      <GoogleUserLocation
          :location="{
              longitude: form.longitude,
              latitude: form.latitude
            }"
          @userLocation="setLocationToForm">
      </GoogleUserLocation>

      <ion-item class="primary-button" lines="none">
        <ion-button expand="block" type="submit">{{ $t('Submit') }}</ion-button>
      </ion-item>
      <ion-item v-if="selectedDraft" class="primary-button">
        <ion-button expand="block" @click="clearDraftAndRedirect()">{{ $t('Clear Draft') }}</ion-button>
      </ion-item>
    </FormControl>
  </DefaultLayout>
</template>

<script>
import DefaultLayout from "@/views/layouts/default.vue"
import CoastHeader from "@/components/CoastHeader.vue"
import FormControl from "@/components/FormControl"
import '@/theme/components/form.scss'
import {
  IonDatetime,
  IonInput,
  IonItem,
  IonRadio,
  IonRadioGroup,
  IonSelect,
  IonSelectOption,
  IonText,
  IonTextarea,
  IonRow,
  IonLabel,
  IonButton
} from '@ionic/vue'
import FieldWrapper from "@/components/FieldWrapper";
import UploadImage from "@/components/UploadImage";
import Calendar from "@/components/icons/Calendar";
import Time from "@/components/icons/Time";
import GoogleUserLocation from "@/components/GoogleUserLocation";
import moment from "moment";
import {mapGetters} from "vuex";
import {alertController, modalController} from "@ionic/core";
import ApiService from "@/services/api.service";
import CongratulationModal from "@/components/CongratulationModal";
import { Device } from '@capacitor/device';
import InteractsWithDrafts from "@/mixins/InteractsWithDrafts";
import cloneDeep from "lodash/cloneDeep";
import ChevronDown from "@/components/icons/ChevronDown.vue";

export default {
  name: "AlgaeWashup",
  mixins: [InteractsWithDrafts],
  components: {
    IonDatetime,
    IonItem,
    IonText,
    IonRadioGroup,
    IonRadio,
    IonSelect,
    IonSelectOption,
    IonTextarea,
    IonInput,
    IonRow,
    IonLabel,
    IonButton,
    FormControl,
    FieldWrapper,
    UploadImage,
    DefaultLayout,
    CoastHeader,
    Calendar,
    Time,
    GoogleUserLocation,
    ChevronDown
  },
  data() {
    return {
      form: {},
      currentDatetime: {
        date: `${moment(new Date()).format('MMMM')} ${moment(new Date()).format('D-Y')}`,
        time: new Date()
      },
      datetime: {
        date: null,
        time: null
      },
      algaeInWater: this.selectedDraft && (this.selectedDraft.form.algaeInWater || this.selectedDraft.form.algae_in_water_length ||this.selectedDraft.algae_in_water_width) ? true : null,
      attachedToObject: null,
      algaeOnBeach: this.selectedDraft && (this.selectedDraft.form.algaeOnBeach || this.selectedDraft.form.algae_on_beach_length ||this.selectedDraft.algae_on_beach_width) ? true : null,
      files: [],
      differentDate: false,
      category: null,
      categories: [
        {
          name: 'Filamentous',
          description: 'Algae that is linked together to form threads or a mesh like mat'
        },
        {
          name: 'Planktonic',
          description: 'Algae that floats in the water column causing water to become green, brown, or reddish'
        },
        {
          name: 'Other/Unknown',
          description: ''
        }
      ],
      colours: [
        'Bright green',
        'Olive green',
        'Red',
        'Pink',
        'Yellow',
        'Orange',
        'Light brown',
        'Dark brown',
        'Dark green',
        'Blue',
        'None of the above'
      ],
      visualObservations: [
        'Slimy',
        'Threads',
        'Glob',
        'Plant like',
        'Silky',
        'Slippery',
        'Mat formation',
        'Net formation',
        'Film',
        'Scum',
        'Foam'
      ],
      odours: [
        'No smell',
        'Septic',
        'Pigpen',
        'Musky',
        'Garlicky',
        'Grassy',
        'Other'
      ],
      environments: [
        'Shoreline',
        'Nearshore water',
        'Wetland',
        'Pond',
        'River',
        'River mouth',
        'Other'
      ],
      errors: []
    }
  },

  computed: {
    ...mapGetters("auth", ["user"]),
    localError () {
      return {
        // "created_at": ["The created at field is required."],
        ...(this.differentDate ? {"submitted_at": ["The submitted at field is required."]} : {}),
        "category": ["The Category field is required."],
        "colour": ["The Algae colour field is required."],
        ...(this.algaeInWater ? {
          "algae_in_water_length": ["The algae in water length field is required."],
          "algae_in_water_width": ["The algae in water width field is required."],
        } : {}),
        ...(this.algaeOnBeach ? {
          "algae_on_beach_length": ["The algae on beach length field is required."],
          "algae_on_beach_width": ["The algae on beach width field is required."],
        } : {})
      };
    }
  },

  created () {
    this.restoreObservationDate();
  },

  beforeRouteLeave(to, from , next) {
     return this.handleBeforeRouteLeave(to, from , next);
  },

  methods: {
    async handleSubmit() {
      const data = new FormData()
      const form = cloneDeep(this.form)


      const deviceInfo = await this.getDeviceInfo();
      data.append('device', JSON.stringify(deviceInfo));

      if (this.datetime && this.datetime.time && this.datetime.date) {
        data.append('submitted_at', this.formattedDates(this.datetime))
      }

      if (this.files) {
        this.files.forEach(image => {
          data.append('files[]', image.file, image.file.name)
        })
      }

      data.append('form_type', this.$options.name)

      if (this.category?.name) {
        if (this.category?.name === 'Other/Unknown' && form?.category_other) {
          form.category = form.category_other
          delete form.category_other
        } else {
          form.category = this.category.name
        }
      }

      data.append('user_id', this.user.id)

      if (form && form.other_environment) {
        form.environment = form.other_environment
        delete form.other_environment
      }

      if (form && form.other_odour) {
        form.odour = form.other_odour
        delete form.other_odour
      }

      if (form && form.other_colour) {
        form.colour = form.other_colour
        delete form.other_colour
      }

      for (const [key, value] of Object.entries(form)) {
        data.append(key, value)
      }

      if (!navigator.onLine) {
        const errKeys = Object.keys(this.localError)
        const formKeys = Array.from(data.keys())
        const diff = errKeys.filter(x => !formKeys.includes(x));
        const errors = {}

        diff.map((index) => {
          errors[index] = this.localError[index]
        })

        this.errors = errors
        this.$refs.FormControl.setErrors(errors)

        if (Object.keys(errors).length) {
          const errorAlert = await alertController.create({
            header: this.$t('Failed to submit'),
            subHeader: this.$t('The data was invalid'),
            message: this.$t('Please fill all the required fields'),
            buttons: [this.$t('OK')]
          });

          await errorAlert.present()

          return
        }
      }

      // If there's no internet, inform the user, save as draft and don't attempt to submit
      await this.checkInternetConnection();

      try {
        await ApiService.post('/submission', data)
            .then(() => {
              this.persistObservationDate()
              this.openModal()
              this.$store.dispatch('auth/getUser')
            })
            .catch(async (err) => {
              const errorAlert = await alertController.create({
                header: this.$t('Failed'),
                subHeader: this.$t('Submission Failed'),
                message: err.response.data.message,
                buttons: [this.$t('OK')]
              });

              if (err.response?.data?.errors) {
                this.$refs.FormControl.setErrors(err.response.data.errors)
                this.errors = err.response.data.errors
              }

              await errorAlert.present()
            })
      } catch (err) {
        const errorAlert = await alertController.create({
          header: this.$t('Failed to submit'),
          subHeader: this.$t('No Internet Connection'),
          message: this.$t('Your report will be stored and sent once you will be online'),
          buttons: [this.$t('OK')]
        });

        await errorAlert.present()
      }
      this.clearDraft();
    },

    formattedDates() {
      if (this.datetime && this.datetime.date && this.datetime.time) {
        const time = new Date(this.datetime.time);
        return moment(this.datetime.date)
            .set('hour', time.getHours())
            .set('minutes', time.getMinutes())
            .set('seconds', time.getSeconds())
            .toISOString()
      }
    },

    formatAMPM(date) {
      let hours = date.getHours();
      let minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      minutes = minutes < 10 ? '0' + minutes : minutes;
      return hours + ':' + minutes + ' ' + ampm;
    },

    setLocationToForm(data) {
      if (data.longitude) {
        this.form.longitude = data.longitude
      }

      if (data.latitude) {
        this.form.latitude = data.latitude
      }
    },

    setImages(data) {
      this.files = [...data]
    },

    toggleDatetime(value) {
      this.differentDate = value
    },

    async openModal() {
      const modal = await modalController
          .create({
            component: CongratulationModal,
            componentProps: {
              resetForm: () => { this.form = {}; this.files = []; },
            },
            cssClass: 'success',
            backdropDismiss:false
          });
      return await modal.present();
    },

    hideError(key) {
      if (key === 'created_at') {
        if (this.dates[0] && this.dates[0].date && this.dates[0].time) {
          delete this.errors[key]
          this.$refs.FormControl.setErrors(this.errors)
        }
      } else {
        delete this.errors[key]
        this.$refs.FormControl.setErrors(this.errors)
      }
    },
    async getDeviceInfo() {
      return await Device.getInfo();
    }
  }
}
</script>

<style scoped>

</style>
